import { UtilityData } from '@tymbe/schema/utility.interface';
import moment, { Moment, MomentInput } from 'moment';

import { DocumentTypeData, PerkData, PersonData, ShiftTemplateData } from '../../../../types/TymbeApi';
import { calculateWorkTime } from '../../../../utils/time';
import StarEmptyIcon from '../icons/StarEmpty';
import StarFullIcon from '../icons/StarFull';
import StarHalfIcon from '../icons/StarHalf';
import { FieldProps } from 'informed';
import apiClient, { ServiceTypes } from '../../../../apiClient';
import { QueryFunctionContext } from 'react-query';
import { Query } from '@feathersjs/feathers';

type Option = PerkData | DocumentTypeData;

export type ShiftsToAdd = {
  date: MomentInput;
  startTime: MomentInput;
  endTime: MomentInput;
  spaces: number;
  shiftTemplate: ShiftTemplateData;
  requirements: Option[] | null;
  utilities: UtilityData[] | null;
  emergencyContact: string;
  invitations: PersonData[];
};

const starIcon = (rating: number) => {
  if (Number.isNaN(rating)) {
    return <StarEmptyIcon className="w-[14px]" />;
  }
  if (rating < 2.5) {
    return <StarEmptyIcon className="w-[14px]" />;
  }
  if (rating < 4) {
    return <StarHalfIcon className="w-[14px]" />;
  }
  return <StarFullIcon className="w-[14px]" />;
};

export const formatJobEval = (jobEval: number) => {
  let rating;
  if (jobEval === 99) rating = (jobEval + 1) * 0.05;
  else rating = jobEval * 0.05;
  return (
    <span className="flex items-center gap-0.5 text-secondary-400 text-xs">
      {!Number.isNaN(rating) ? (Math.round(rating * 100) / 100).toFixed(1) : 'N/A'}
      {starIcon(rating)}
    </span>
  );
};

export const validateMaxDuration = (startTime?: Moment, endTime?: Moment) => {
  if (!startTime || !endTime) return undefined;
  const duration = calculateWorkTime(startTime, endTime, true, 'hours');
  if (duration > 13) {
    return 'Maximální délka směny je 13 hodin';
  }
  if (duration < 0) {
    return 'Začátek směny nemůže být po konci';
  }
  return undefined;
};

export const maxDuration: FieldProps['validate'] = (_, state, { formApi, scope }) => {
  const { startTime, endTime } = scope ? formApi.getValue(scope) : state;
  const validTime = /^([01]?\d|2[0-3]):([0-5]\d)$/;
  if (!validTime.test(startTime) || !validTime.test(endTime)) {
    return undefined;
  }
  const start = moment(startTime, 'HH:mm');
  const addDay = startTime.padStart(5, 0) > endTime.padStart(5, 0) ? 1 : 0;
  const end = moment(endTime, 'HH:mm').add(addDay, 'day');
  return validateMaxDuration(start, end);
}

export const queryFn = <S extends keyof ServiceTypes>({ queryKey: [service, id, query] }: QueryFunctionContext<readonly [S, ServiceTypes[S]['id'], Query?]>): ReturnType<ServiceTypes[S]['get']>  => {
  return apiClient.service(service).get(id, { query }) as ReturnType<ServiceTypes[S]['get']>;
}

export const getDurationString = (start?: string, end?: string) => {
  if (!start || !end) return '0 h 0 m';

  const [startHours = 0, startMinutes = 0] = start.split(':');
  const [endHours = 0, endMinutes = 0] = end.split(':');

  const startTime = moment({ hour: Number(startHours), minute: Number(startMinutes) });
  const endTime = moment({ hour: Number(endHours), minute: Number(endMinutes) });

  if (endTime.isBefore(startTime)) {
    endTime.add(1, 'day');
  }

  const duration = moment.duration(endTime.diff(startTime));

  return `${duration.hours()} h ${duration.minutes()} m`;
}
